import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  array, bool, func, object, oneOf, shape, string,
} from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import AdultContentModal from 'Pods/Shared/LazyAdultContentModal'
import ModelCard from 'Pods/Shared/ModelCard/ModelCard'
import getModelsPerRow from 'Pods/Shared/ModelCard/utils/getModelsPerRow'

import getArrayFilters from 'Utils/getArrayFilters'
import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'


function FreeItemsSection({
  heading = '',
  data = EMPTY_ARRAY,
  cart = EMPTY_OBJECT,
  extractingCoreStyles = false,
  cardVisibilitySettings,
  cardSizeSetting,
  showCgtVerification = true,
  viewerIsAdult = false,
  setViewerIsAdult = NOOP,
  selectedFilters = EMPTY_OBJECT,
  setSelectedFilters = NOOP,
  trackFilterChange = NOOP,
  isSimilarSearchPossible = false,
  isSalesBoostEnabled = false,
  subscriptionBadgesEnabled = false,
  subscriptionFlowEnabled = false,
}) {
  const [cartItemIds, setCartItemIds] = useState(cart.cartItemIds || EMPTY_ARRAY)
  const [wishListItems, setWishListItems] = useState(EMPTY_ARRAY)
  const [showGallery, setShowGallery] = useState()
  const [galleryToShow, setGalleryToShow] = useState()
  const [isAdultContentModalVisible, toggleModal] = useState(false)
  const currentBreakpoint = useBreakpoint()
  const modelsPerRow = getModelsPerRow(currentBreakpoint, cardSizeSetting)

  useEffect(() => {
    setCartItemIds(cart.cartItemIds || EMPTY_ARRAY)
  }, [cart])

  // Mirror List: defer opening the gallery to the next render so the previously
  // open preview tears down first (ModelCard reads showGallery synchronously).
  useEffect(() => {
    setShowGallery(galleryToShow)
  }, [galleryToShow])

  const handlePreviewClick = useCallback((previewProps) => {
    setShowGallery(undefined)
    setGalleryToShow({ ...previewProps })
  }, [])

  const handleCloseGallery = useCallback(() => setShowGallery(undefined), [])
  const showAdultModal = useCallback(() => toggleModal(true), [])
  const hideAdultModal = useCallback(() => toggleModal(false), [])

  const currentSelectedFileTypes = useMemo(
    () => getArrayFilters(selectedFilters.fileTypes || EMPTY_ARRAY),
    [selectedFilters.fileTypes]
  )

  if (!extractingCoreStyles && (!heading || !data.length)) {
    return null
  }

  return (
    <div className="col-12 cgt-mt-20">
      <h2 className="cgt-legacy-heading-h4 cgt-mb-20">
        {heading}
      </h2>
      <div className="row">
        {data.map((model, index) => (
          <ModelCard
            key={`free-item-${model.id}-${index}`}
            {...model}
            cart={cart}
            modelIndex={index}
            listIndex={index}
            modelsPerRow={modelsPerRow}
            lastModelIndex={data.length - 1}
            totalCount={data.length}
            viewerIsAdult={viewerIsAdult}
            setViewerIsAdult={setViewerIsAdult}
            showGallery={showGallery}
            showAdultModal={showAdultModal}
            onPreviewClick={handlePreviewClick}
            onCloseGallery={handleCloseGallery}
            trackLastItemInView={NOOP}
            currentSelectedFileTypes={currentSelectedFileTypes}
            setSelectedFilters={setSelectedFilters}
            trackFilterChange={trackFilterChange}
            cardVisibilitySettings={cardVisibilitySettings}
            cardSizeSetting={cardSizeSetting}
            setCartItemIds={setCartItemIds}
            cartItemIds={cartItemIds}
            wishListItems={wishListItems}
            setWishListItems={setWishListItems}
            showCgtVerification={showCgtVerification}
            searchingFree
            isSimilarSearchPossible={isSimilarSearchPossible}
            isSalesBoostEnabled={isSalesBoostEnabled}
            subscriptionBadgesEnabled={subscriptionBadgesEnabled}
            subscriptionFlowEnabled={subscriptionFlowEnabled}
            extractingCoreStyles={extractingCoreStyles}
          />
        ))}
      </div>
      {isAdultContentModalVisible && (
        <AdultContentModal setViewerIsAdult={setViewerIsAdult} hideAdultModal={hideAdultModal} />
      )}
    </div>
  )
}

FreeItemsSection.propTypes = {
  heading: string,
  data: array,
  cart: object,
  extractingCoreStyles: bool,
  cardVisibilitySettings: shape({
    cgtStandard: bool,
    format: bool,
    price: bool,
    quickActionsOnHover: bool,
    type: bool,
    title: bool,
  }).isRequired,
  cardSizeSetting: oneOf(['small', 'medium', 'large']).isRequired,
  showCgtVerification: bool,
  viewerIsAdult: bool,
  setViewerIsAdult: func,
  selectedFilters: object,
  setSelectedFilters: func,
  trackFilterChange: func,
  isSimilarSearchPossible: bool,
  isSalesBoostEnabled: bool,
  subscriptionBadgesEnabled: bool,
  subscriptionFlowEnabled: bool,
}

function mapStateToProps({
  cardVisibilitySettings,
  cardSizeSetting,
  showCgtVerification,
  viewerIsAdult,
  setViewerIsAdult,
  selectedFilters,
  setSelectedFilters,
  trackFilterChange,
  isSimilarSearchPossible,
  subscriptionFlowEnabled,
}) {
  return {
    cardVisibilitySettings,
    cardSizeSetting,
    showCgtVerification,
    viewerIsAdult,
    setViewerIsAdult,
    selectedFilters,
    setSelectedFilters,
    trackFilterChange,
    isSimilarSearchPossible,
    subscriptionFlowEnabled,
  }
}

export default WithTriangleUiContext(mapStateToProps)(FreeItemsSection)
